import { useEffect, useId, type MouseEvent } from 'react';
import { useCompany } from '@/company/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import connectionsCopy from '@/i18n/ns/connections';
import { formatCount } from '@/i18n/format';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { writeWorkspaceHash } from '@/lib/chat-links';
import { WorkspaceIcon } from './icons';
import { routeFor } from './navigation';
import { AddressField } from './agent-connections/address';
import { agentAccessApi } from './agent-connections/api';
import {
  agentsSummary,
  grantState,
  methodLabel,
  statusLine,
  withNames,
} from './agent-connections/format';
import { useNow, useScopedResource } from './agent-connections/use-scoped';
import type { KnowledgeClientGrant } from './agent-connections/types';

/** The native chat tool whose result this card stands for. */
export const EXTERNAL_MCP_TOOL = 'read_external_mcp';

/** Connections with the External MCP setup opened, as Knowledge links it. */
export const SETUP_LINK = `${routeFor('connections')}?setup=knowledge-client`;

/** Opens the Connections setup in place; a modified or non-primary click keeps the browser's own behaviour. */
export function openSetupLink(event: MouseEvent<HTMLAnchorElement>) {
  if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  writeWorkspaceHash(SETUP_LINK);
}
const SHOWN_AGENTS = 4;
const TONE = { ready: 'ok', waiting: 'waiting', stale: 'stale' } as const;

/**
 * What the card takes from the tool's answer: only that a status read
 * happened. The transcript keeps that answer as it was when the model read
 * it, so the address, its warnings and the agents shown are the viewer's own
 * live state, read the way Connections reads it. An agent disconnected since
 * is not shown as connected, and nothing from the stored answer is rendered.
 */
export interface ExternalMcpPointer {
  status: 'ready';
  enabled: true;
}

export function isExternalMcpPointer(value: unknown): value is ExternalMcpPointer {
  if (typeof value !== 'object' || value === null) return false;
  const pointer = value as { status?: unknown; enabled?: unknown };
  return pointer.status === 'ready' && pointer.enabled === true;
}

function AgentRow({ grant, now }: { grant: KnowledgeClientGrant; now: number }) {
  const copy = useNamespaceCopy(connectionsCopy);
  const locale = useChatInterfaceLocale();
  const state = grantState(grant);
  return (
    <li className="ws-mcp-card-agent" data-status={state}>
      <span className="ws-mcp-card-dot" data-tone={TONE[state]} aria-hidden />
      <span className="ws-mcp-card-name">{grant.clientName}</span>
      <span className="ws-mcp-card-meta">
        <span className="ws-sr-only">: </span>
        {methodLabel(grant, copy)}
        <span aria-hidden> · </span>
        <span className="ws-sr-only">, </span>
        {statusLine(grant, now, copy, locale)}
      </span>
    </li>
  );
}

function Agents({ clients, now }: { clients: KnowledgeClientGrant[]; now: number }) {
  const copy = useNamespaceCopy(connectionsCopy);
  const locale = useChatInterfaceLocale();
  if (clients.length === 0) return null;
  const more = clients.length - SHOWN_AGENTS;
  return (
    <ul className="ws-mcp-card-agents" aria-label={copy.cardAgentsLabel}>
      {clients.slice(0, SHOWN_AGENTS).map((grant) => (
        <AgentRow key={grant.grantId} grant={grant} now={now} />
      ))}
      {more > 0 && (
        <li className="ws-mcp-card-more">
          {formatCopy(copy.cardMore, { more: formatCount(copy.cardMoreCount, more, locale) })}
        </li>
      )}
    </ul>
  );
}

/**
 * External MCP in the conversation: the one address to paste into an
 * external agent, what the server says about reaching it, the person's own
 * connected agents with their live status, and the way to the full setup.
 */
export function ExternalMcpCard() {
  const heading = useId();
  const copy = useNamespaceCopy(connectionsCopy);
  const locale = useChatInterfaceLocale();
  // The company is the CURRENT one at render time, never read from the tool's stored output.
  const company = useCompany();
  const companyName = company.data?.company?.name;
  const access = useScopedResource(agentAccessApi.list, copy.agentsLoadFailed);
  const { data, error } = access;
  // An authoritative off hides everything read before it: no address, no grants.
  const enabled = !access.disabled && data?.enabled === true;
  const clients = enabled ? data.clients.map((grant) => withNames(grant, copy)) : [];
  const now = useNow(60_000, clients.length > 0);

  // A person usually finishes signing in in another app or tab, then returns.
  useEffect(() => {
    const onFocus = () => void access.refresh().catch(() => false);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [access.scope]);

  const loading = !data && !error && !access.disabled;

  return (
    <section className="ws-mcp-card" aria-labelledby={heading}>
      <header className="ws-mcp-card-head">
        <span className="ws-mcp-card-mark" aria-hidden>
          <WorkspaceIcon name="connections" size={15} />
        </span>
        <span className="ws-mcp-card-title">
          <strong id={heading}>{copy.connectedAgentsTitle}</strong>
          <small>
            {enabled && clients.length > 0
              ? agentsSummary(clients, copy, locale)
              : companyName
                ? formatCopy(copy.connectedAgentsSubtitle, { company: companyName })
                : copy.connectedAgentsSubtitleAny}
          </small>
        </span>
        {(enabled || loading) && (
          <a className="ws-mcp-card-open" href={SETUP_LINK} onClick={openSetupLink}>
            {copy.cardOpenSetup}
            <WorkspaceIcon name="arrow" size={13} />
          </a>
        )}
      </header>
      {loading && (
        <p className="ws-mcp-card-quiet" role="status">
          {copy.cardChecking}
        </p>
      )}
      {access.disabled && (
        <p className="ws-mcp-card-quiet" role="status">
          {copy.connectingAgentsOff}
        </p>
      )}
      {!data && error && !access.disabled && (
        <p className="ws-mcp-card-quiet" role="alert">
          {error}{' '}
          <button type="button" className="ws-mcp-card-retry" onClick={access.retry}>
            {copy.cardTryAgain}
          </button>
        </p>
      )}
      {data && !enabled && (
        <p className="ws-mcp-card-quiet" role="status">
          {copy.connectingAgentsOff}
        </p>
      )}
      {enabled && (
        <>
          <div className="ws-mcp-card-address">
            <span className="ws-mcp-card-label">{copy.agentsAddressTitle}</span>
            <AddressField address={data.address} />
          </div>
          <Agents clients={clients} now={now} />
        </>
      )}
    </section>
  );
}
